/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import ClickAwayListener from '@ion/dls/src/components/listeners/ClickAwayListener';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {AgGridType} from '@ion/dls/src/grid/IonGrid';

export interface CellTooltipWrapperProps {
  data: string[];
  gridApi: AgGridType;
  columnName: string;
};

const itemSections = {
  alignItems     : 'center',
  cursor         : 'pointer',
  width          : '100%',
  justifyContent : 'left'
};

const useStyles = makeStyles(() => ({
  itemSection : {
    ...itemSections,
    display : 'inline-flex'
  },
  itemSectionTitle : {
    ...itemSections,
    display : 'flex'
  }
}));

/**
 * Renders a table cell with a dynamic tooltip that displays overflowing items.
 *
 * This component is intended for use within data grids where cell content may exceed
 * the visible width. It measures the current column width, determines which items fit
 * within that space, and shows remaining items in a tooltip when hovered.
 *
 * // Example usage
 * <CellTooltipWrapper
 *   data={['Tag1', 'Tag2', 'Tag3']}
 *   gridApi={gridApiInstance}
 *   columnName="status"
 * />
 *
 * // Behavior
 * // - Displays visible tags within column width.
 * // - Shows all tags inside a tooltip when hovered if space is insufficient.
 *
 * Internally, the component:
 *  - Tracks column width using the provided `gridApi`.
 *  - Calculates which items fit based on text length and width.
 *  - Displays truncated items with "..." if available space is exceeded.
 *  - Shows a tooltip listing all items on hover.
 */
const CellTooltipWrapper = ({
  data,
  gridApi,
  columnName
}: CellTooltipWrapperProps) => {
  const classes                       = useStyles();
  const [openItems, setOpenItems]     = useState(false);
  const [columnWidth, setColumnWidth] = useState(0);

  useEffect(() => {
    const calculateVisibleItems = () => {
      const columnWidths = gridApi?.getColumnState()?.find(column => column?.colId === columnName)?.width;
      if (columnWidths) {
        setColumnWidth(columnWidths);
      }
    };

    calculateVisibleItems();
    gridApi?.addEventListener('columnResized', calculateVisibleItems);

    return () => {
      gridApi?.removeEventListener('columnResized', calculateVisibleItems);
    };
  }, [gridApi]);

  const toggleTooltip = useCallback((open = false) => (e) => {
    e?.preventDefault();
    setOpenItems(open);
  }, [setOpenItems]);

  const getItemsTotalWidth = useCallback((data) => {
    return data?.reduce?.((total, item) => total + item?.length, 0);
  }, [data]);

  const getDisplayedItems = useCallback((data) => {
    const paddingWidth   = 12;
    const marginWidth    = 4;
    const titleWidth     = 7;
    let currentWidth     = 0;
    const displayedItems = [];
    const remainingItems = [];

    const totalItemsWidth = getItemsTotalWidth(data) * titleWidth;

    if (totalItemsWidth <= columnWidth) {
      return {
        displayedItems : data,
        remainingItems : []
      };
    }

    data?.forEach?.((item, index) => {
      const itemTitleWidth = (item?.length ?? 0) * titleWidth + paddingWidth + marginWidth;
      const exceedsWidth   = currentWidth + itemTitleWidth > columnWidth;

      if (exceedsWidth && displayedItems?.length) {
        remainingItems.push(item);
        return;
      }

      currentWidth += itemTitleWidth;
      displayedItems.push(item);

      if (displayedItems?.length === 1 && index === 0 && data?.length > 1) {
        remainingItems.shift();
      }
    });

    return {
      displayedItems,
      remainingItems
    };
  }, [columnWidth, data]);

  const {
    displayedItems,
    remainingItems
  } = getDisplayedItems(data) || {};
  
  if (Array.isArray(data) && data?.length) {
    return (
      <>
        <ClickAwayListener mountListener onClickAway={toggleTooltip()}>
          <Tooltip arrow interactive
            open={openItems}
            onClose={toggleTooltip()}
            title={
              <>
                {data?.map?.((item, index) => (
                  <div key={index}
                    className={classes.itemSectionTitle}>
                    <span>{`\u2022 ${item}`}</span>
                  </div>
                ))}
              </>
            }
            placement='bottom'>
            <div className={classes.itemSection}
              onMouseOver={toggleTooltip(true)}>
              {displayedItems?.map?.((item, index) => {
                return (
                  <div key={index}>
                    {`${index !== 0 ? ',' : ''} ${item}`}
                  </div>
                );
              })}
              {remainingItems?.length >= 1 && (
                <span>...</span>
              )}
            </div>
          </Tooltip>
        </ClickAwayListener>
      </>
    );
  };

  return <div />;
};

export default CellTooltipWrapper;
